/**
 * PermissionMatrix Component
 *
 * A permission assignment UI that displays an action x subject matrix.
 * Permissions are grouped by domain/subdomain for organization.
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { Action, Subject } from '@erp/authz';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import {
  CheckCircleIcon,
  XCircleIcon,
  CircleNotchIcon,
  ArrowCounterClockwiseIcon,
  FloppyDiskIcon,
  ShieldCheckIcon,
  CaretDownIcon,
} from '@phosphor-icons/react';

interface PermissionMatrixProps {
  groupId: string;
  groupName: string;
  onSaveSuccess?: () => void;
  /** If true, hides the header with save/reset buttons (for embedded use with external controls) */
  hideHeader?: boolean;
  /** Render prop for external save/reset buttons - receives hasChanges, isSaving, onSave, onReset */
  renderFooter?: (props: {
    hasChanges: boolean;
    isSaving: boolean;
    onSave: () => void;
    onReset: () => void;
    enabledCount: number;
  }) => React.ReactNode;
}

interface PermissionState {
  [key: string]: boolean; // key format: "action:subject"
}

function permissionKey(action: Action, subject: Subject): string {
  return `${action}:${subject}`;
}

// Human-readable action labels
const ACTION_LABELS: Record<string, string> = {
  create: 'Create',
  read: 'View',
  update: 'Edit',
  delete: 'Delete',
  approve: 'Approve',
  execute: 'Execute',
  report: 'Report',
  manage: 'Manage',
};

// Human-readable subject labels (convert PascalCase to spaces)
function formatSubject(subject: string): string {
  return subject.replace(/([A-Z])/g, ' $1').trim();
}

export function PermissionMatrix({
  groupId,
  groupName,
  onSaveSuccess,
  hideHeader = false,
  renderFooter,
}: PermissionMatrixProps) {
  const queryClient = useQueryClient();

  // Fetch available permissions (actions, subjects, domain groups)
  const {
    data: availableData,
    isLoading: availableLoading,
    error: availableError,
  } = useQuery(orpc.authz.listAvailablePermissions.queryOptions());

  // Fetch current permissions for this group
  const {
    data: groupData,
    isLoading: groupLoading,
    error: groupError,
  } = useQuery(
    orpc.authz.getGroupPermissions.queryOptions({
      input: { groupId },
    }),
  );

  // Local state for permission checkboxes
  const [permissionState, setPermissionState] = useState<PermissionState>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize permission state from loaded data
  useMemo(() => {
    if (groupData?.permissions) {
      const initialState: PermissionState = {};
      for (const perm of groupData.permissions) {
        initialState[permissionKey(perm.action, perm.subject)] = true;
      }
      setPermissionState(initialState);
      setHasChanges(false);
    }
  }, [groupData?.permissions]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (permissions: { action: Action; subject: Subject }[]) => {
      return orpc.authz.setGroupPermissions.call({
        groupId,
        permissions: permissions.map((p) => ({
          action: p.action,
          subject: p.subject,
          domain: null,
          subdomain: null,
          description: null,
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.authz.getGroupPermissions.queryOptions({
          input: { groupId },
        }).queryKey,
      });
      setHasChanges(false);
      onSaveSuccess?.();
    },
  });

  // Toggle a permission
  function handleToggle(action: Action, subject: Subject) {
    const key = permissionKey(action, subject);
    setPermissionState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setHasChanges(true);
  }

  // Toggle all permissions for a subject (row)
  function handleToggleRow(subject: Subject, actions: Action[]) {
    const allEnabled = actions.every(
      (action) => permissionState[permissionKey(action, subject)],
    );
    const newState = { ...permissionState };
    for (const action of actions) {
      newState[permissionKey(action, subject)] = !allEnabled;
    }
    setPermissionState(newState);
    setHasChanges(true);
  }

  // Save changes
  function handleSave() {
    const permissions: { action: Action; subject: Subject }[] = [];
    for (const [key, enabled] of Object.entries(permissionState)) {
      if (enabled) {
        const [action, subject] = key.split(':') as [Action, Subject];
        permissions.push({ action, subject });
      }
    }
    saveMutation.mutate(permissions);
  }

  // Reset to original state
  function handleReset() {
    if (groupData?.permissions) {
      const initialState: PermissionState = {};
      for (const perm of groupData.permissions) {
        initialState[permissionKey(perm.action, perm.subject)] = true;
      }
      setPermissionState(initialState);
      setHasChanges(false);
    }
  }

  // Count enabled permissions
  const enabledCount = Object.values(permissionState).filter(Boolean).length;

  // Loading state
  if (availableLoading || groupLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-6 w-6" />
        <span className="ml-2 text-muted-foreground">
          Loading permissions...
        </span>
      </div>
    );
  }

  // Error state
  if (availableError || groupError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-destructive">
        <XCircleIcon className="h-8 w-8 mb-2" />
        <p className="font-medium">Error loading permissions</p>
        <p className="text-sm text-muted-foreground mt-1">
          Please try refreshing the page
        </p>
      </div>
    );
  }

  if (!availableData || !groupData) {
    return null;
  }

  const { actions, domainGroups, validActionsPerSubject } = availableData;

  // Filter out Manage action (reserved for Gently)
  const displayActions = actions.filter((a) => a !== Action.Manage);

  // Helper to check if an action is valid for a subject
  function isActionValidForSubject(action: Action, subject: Subject): boolean {
    const validActions = validActionsPerSubject[subject];
    return validActions?.includes(action) ?? false;
  }

  return (
    <div className="flex flex-col">
      {/* Header with stats and actions - only shown if not hidden */}
      {!hideHeader && (
        <div className="shrink-0 flex items-center justify-between gap-4 pb-4 border-b mb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
              <ShieldCheckIcon className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Permission Configuration</p>
              <p className="text-xs text-muted-foreground">
                <Badge variant="secondary" className="mr-1.5 text-xs h-5">
                  {enabledCount}
                </Badge>
                permission{enabledCount !== 1 ? 's' : ''} enabled
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={!hasChanges || saveMutation.isPending}
              className="h-8"
            >
              <ArrowCounterClockwiseIcon className="h-3.5 w-3.5 mr-1.5" />
              Reset
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || saveMutation.isPending}
              className="h-8 min-w-[100px]"
            >
              {saveMutation.isPending ? (
                <>
                  <CircleNotchIcon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Saving...
                </>
              ) : saveMutation.isSuccess && !hasChanges ? (
                <>
                  <CheckCircleIcon className="h-3.5 w-3.5 mr-1.5" />
                  Saved
                </>
              ) : (
                <>
                  <FloppyDiskIcon className="h-3.5 w-3.5 mr-1.5" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Domain accordions */}
      <Accordion
        type="multiple"
        defaultValue={domainGroups.map((d) => d.domain)}
        className="w-full"
      >
        {domainGroups.map((domain) => (
          <AccordionItem
            key={domain.domain}
            value={domain.domain}
            className="border rounded-lg mb-2 px-3 data-[state=open]:bg-muted/30"
          >
            <AccordionTrigger className="py-3 hover:no-underline">
              <div className="flex items-center gap-2">
                <span className="font-medium">{domain.domain}</span>
                <Badge variant="outline" className="text-xs h-5 font-normal">
                  {domain.subdomains.reduce(
                    (acc, s) => acc + s.subjects.length,
                    0,
                  )}{' '}
                  resources
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="space-y-6">
                {domain.subdomains.map((subdomain) => (
                  <div key={subdomain.subdomain}>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                      {subdomain.subdomain}
                    </h4>

                    {/* Permission matrix table */}
                    <div className="overflow-x-auto rounded-lg border bg-card">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left py-2.5 px-3 font-medium text-muted-foreground w-[200px]">
                              Resource
                            </th>
                            {displayActions.map((action) => (
                              <th
                                key={action}
                                className="px-3 py-2.5 text-center font-medium text-muted-foreground w-[80px]"
                              >
                                {ACTION_LABELS[action] ?? action}
                              </th>
                            ))}
                            <th className="w-[60px]"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {subdomain.subjects.map((subject, idx) => {
                            // Get valid actions for this subject
                            const validActionsForSubject =
                              displayActions.filter((action) =>
                                isActionValidForSubject(action, subject),
                              );
                            const rowEnabled = validActionsForSubject.filter(
                              (action) =>
                                permissionState[permissionKey(action, subject)],
                            ).length;
                            const allEnabled =
                              validActionsForSubject.length > 0 &&
                              rowEnabled === validActionsForSubject.length;

                            return (
                              <tr
                                key={subject}
                                className={cn(
                                  'group transition-colors',
                                  idx !== subdomain.subjects.length - 1 &&
                                    'border-b',
                                  'hover:bg-muted/30',
                                )}
                              >
                                <td className="py-2.5 px-3">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {formatSubject(subject)}
                                    </span>
                                    {rowEnabled > 0 && (
                                      <Badge
                                        variant="secondary"
                                        className="text-[10px] h-4 px-1.5"
                                      >
                                        {rowEnabled}
                                      </Badge>
                                    )}
                                  </div>
                                </td>
                                {displayActions.map((action) => {
                                  const isValidAction = isActionValidForSubject(
                                    action,
                                    subject,
                                  );
                                  const key = permissionKey(action, subject);
                                  const isChecked =
                                    permissionState[key] || false;

                                  // Show empty cell for invalid action/subject combinations
                                  if (!isValidAction) {
                                    return (
                                      <td
                                        key={action}
                                        className="px-3 py-2.5 text-center"
                                      >
                                        <div className="flex justify-center">
                                          <span className="text-muted-foreground/30">
                                            —
                                          </span>
                                        </div>
                                      </td>
                                    );
                                  }

                                  return (
                                    <td
                                      key={action}
                                      className="px-3 py-2.5 text-center"
                                    >
                                      <div className="flex justify-center">
                                        <Checkbox
                                          checked={isChecked}
                                          onCheckedChange={() =>
                                            handleToggle(action, subject)
                                          }
                                          disabled={saveMutation.isPending}
                                          className={cn(
                                            'transition-all',
                                            isChecked &&
                                              'bg-primary border-primary data-[state=checked]:bg-primary',
                                          )}
                                        />
                                      </div>
                                    </td>
                                  );
                                })}
                                <td className="px-2 py-2.5">
                                  {validActionsForSubject.length > 0 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={() =>
                                        handleToggleRow(
                                          subject,
                                          validActionsForSubject,
                                        )
                                      }
                                      disabled={saveMutation.isPending}
                                    >
                                      {allEnabled ? 'None' : 'All'}
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Save error message */}
      {saveMutation.isError && (
        <div className="shrink-0 flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-lg p-3 mt-4">
          <XCircleIcon className="h-4 w-4 shrink-0" />
          Failed to save permissions. Please try again.
        </div>
      )}

      {/* Unsaved changes indicator - only show if header is visible */}
      {!hideHeader && hasChanges && (
        <div className="shrink-0 flex items-center justify-between gap-2 text-sm bg-amber-500/10 text-amber-700 dark:text-amber-400 rounded-lg p-3 border border-amber-500/20 mt-4">
          <span>You have unsaved changes</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="h-7 text-xs border-amber-500/30 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
          >
            Save Now
          </Button>
        </div>
      )}

      {/* External footer via render prop */}
      {renderFooter?.({
        hasChanges,
        isSaving: saveMutation.isPending,
        onSave: handleSave,
        onReset: handleReset,
        enabledCount,
      })}
    </div>
  );
}
